import React, { forwardRef, useState } from "react";
import classNames from "classnames";
import { Logo } from "@campgladiator/cgui-core.atoms.logo";
import { breakpoints } from "@campgladiator/cgui-core.utilities.helpers.resolution-variables";
import { Button, ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { useDeviceWidth } from "@campgladiator/cgui-core.utilities.hooks.use-device-width";
import { Avatar, AvatarProps } from "@campgladiator/cgui-core.atoms.avatar";
import { Tooltip } from "@campgladiator/cgui-core.atoms.tooltip";
import { Icon } from "@campgladiator/cgui-core.atoms.icon";
import {
  ListMenuProps,
  ListMenu,
} from "@campgladiator/cgui-core.molecules.list-menu";
import { Paragraph } from "@campgladiator/cgui-core.atoms.typography";
import { InputProps } from "@campgladiator/cgui-core.atoms.input";
import { generateSandwichMenu } from "./generateSandwichMenu";
import { HeaderSearchBar } from "./components/header-search-bar";
import { AccessoryItem, AccessoryItemProps } from "./components/accessory-item";
import styles from "./header.module.scss";

type MenuItemsProps = {
  headerLinks: HeaderLinksProps[];
};

type Theme = "core" | "trainer";

type SandwichButtonProps = {
  isLoggedIn?: boolean;
  mappedItems: ListMenuProps["items"];
  theme?: Theme;
};

type UserButtonProps = {
  avatar?: AvatarProps;
  items: ListMenuProps["items"];
  onLogoutClick?: () => void;
  userName?: string;
};

export type SearchBarProps = {
  changeOnKeyPress?: React.KeyboardEvent<HTMLInputElement>["key"];
  className?: string;
  inputSize?: InputProps["inputSize"];
  onSearchValueChange: (searchInputValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autofocus?: boolean;
};

export type HeaderLinksProps = {
  isActiveRoute?: boolean;
  items?: ListMenuProps["items"];
  label: string;
  link: string;
  newTab?: boolean;
  isHidden?: boolean;
};

export type GenerateSandwichMenuArgs = {
  headerLinks: HeaderLinksProps[] | AccessoryItemProps[];
  isLoggedIn: boolean;
  loginUrl?: string;
};

const MenuItemsLine = (item: HeaderLinksProps, index: number) => {
  const [show, setShow] = useState<boolean>(false);
  const isActiveRoute = item.isActiveRoute;
  return (
    <li
      className={classNames(styles.item, {
        [styles.show]: show,
      })}
      key={index}
      onClick={() => setShow(!show)}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <a
        target={item.newTab ? "_blank" : "_self"}
        className={styles.link}
        href={item.link}
      >
        <Paragraph
          size="small"
          style={isActiveRoute ? { color: "#333333" } : {}}
          weight={isActiveRoute ? "normal" : "book"}
        >
          {item.label}
        </Paragraph>
      </a>
      {item.items && (
        <Icon.Line name="icon-angle-down" className={styles.arrowIcon} />
      )}
      {item.items && (
        <ListMenu
          items={item.items}
          className={classNames(styles.itemListHeader, {
            [styles.show]: show,
          })}
        />
      )}
    </li>
  );
};

const MenuItems = ({ headerLinks }: MenuItemsProps) => {
  return (
    <div className={styles.headerLinksContainer}>
      <ul className={styles.list}>
        {headerLinks
          ?.filter((item) => !item.isHidden)
          .map((item, index) => MenuItemsLine(item, index))}
      </ul>
    </div>
  );
};

const SandwichButton = ({
  mappedItems,
  isLoggedIn,
  theme,
}: SandwichButtonProps) => {
  const [show, setShow] = useState<boolean>(false);
  return (
    <div
      className={classNames(
        styles.sandwichButton,
        isLoggedIn ? styles.isLoggedIn : styles.isNotLoggedIn,
        {
          [styles.show]: show,
        }
      )}
      onClick={() => setShow(!show)}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button className={classNames(styles.item)}>
        <Icon.Line name={"icon-bars"} />
      </button>
      <ListMenu
        items={mappedItems}
        className={classNames(styles.itemListHeader, {
          [styles.show]: show,
        })}
        theme={theme}
      />
    </div>
  );
};

export type HeaderButtonProps = {
  buttonType?: "action" | "link";
  label: string;
} & ButtonProps;

const HeaderButton = (props: HeaderButtonProps) => {
  const deviceWidth = useDeviceWidth();
  let size = "small";
  if (deviceWidth < breakpoints.tabletSmall) size = "xsmall";
  return (
    <div className={styles.buttonContainer}>
      <Button {...props} size={size as any}>
        {props.label}
      </Button>
    </div>
  );
};

const HeaderAvatar = ({
  avatar,
  items,
  onLogoutClick,
  userName,
}: UserButtonProps) => {
  const { tabletMedium } = breakpoints;
  const deviceWidth = useDeviceWidth();
  const avatarSize = deviceWidth > tabletMedium ? "30px" : "24px";
  const [show, setShow] = useState<boolean>(false);
  const logoutItem: ListMenuProps["items"] = [
    { type: "divider" },
    {
      type: "item",
      text: "Log Out",
      itemId: "6",
      onClick: onLogoutClick,
    },
  ];

  return (
    <div className={styles.userButtonContainer}>
      <div
        className={classNames(styles.userButton, {
          [styles.show]: show,
        })}
        onClick={() => setShow(!show)}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <Avatar size={avatarSize} src={avatar?.src} />
        {userName && <Paragraph size="small">{userName}</Paragraph>}
        <Icon.Line name="icon-angle-down" className={styles.arrowIcon} />
        {!!items && (
          <ListMenu
            items={items?.concat(logoutItem)}
            className={classNames(styles.itemListHeader, {
              [styles.show]: show,
            })}
          />
        )}
      </div>
    </div>
  );
};

type HeaderButtonsProps = {
  buttons: HeaderButtonProps[];
};

const HeaderButtons = ({ buttons }: HeaderButtonsProps) => {
  return (
    <>
      {buttons.map((buttonProps, index) => (
        <HeaderButton {...buttonProps} key={index} />
      ))}
    </>
  );
};

type CollapsedHeaderProps = {
  accessoryItem?: AccessoryItemProps;
  buttons: HeaderButtonProps[];
  deviceWidth: number;
  headerLinks: HeaderLinksProps[];
  isLoggedIn?: boolean;
  loginUrl: string;
  onLogoutClick?: () => void;
  theme?: Theme;
  userButton?: UserButtonProps;
  searchBar?: SearchBarProps;
};

const CollapsedHeader = forwardRef<HTMLInputElement, CollapsedHeaderProps>(
  (
    {
      headerLinks,
      isLoggedIn = false,
      deviceWidth,
      accessoryItem,
      buttons,
      userButton,
      loginUrl,
      theme,
      onLogoutClick,
      searchBar,
    }: CollapsedHeaderProps,
    ref
  ) => {
    const { tabletMedium, tabletLarge } = breakpoints;
    const exceedsTabletSize =
      deviceWidth > (searchBar ? tabletLarge : tabletMedium);
    const shouldAddAccessoryItem = exceedsTabletSize || !accessoryItem;
    const showAccessoryItem = accessoryItem && exceedsTabletSize;
    const isLoggedInAndHasUserButton = isLoggedIn && userButton;

    const mappedItems = generateSandwichMenu({
      headerLinks: shouldAddAccessoryItem
        ? headerLinks
        : [...headerLinks, accessoryItem],
      isLoggedIn,
      loginUrl,
    });

    return (
      <div className={styles.collapsedMenuItemsContainer}>
        {searchBar && <HeaderSearchBar {...searchBar} ref={ref} />}
        {showAccessoryItem && <AccessoryItem {...accessoryItem} />}
        {isLoggedInAndHasUserButton && (
          <HeaderAvatar {...userButton} onLogoutClick={onLogoutClick} />
        )}
        <HeaderButtons buttons={buttons} />
        {mappedItems.length !== 0 && (
          <SandwichButton
            mappedItems={mappedItems}
            isLoggedIn={isLoggedIn}
            theme={theme}
          />
        )}
      </div>
    );
  }
);

type FullHeaderProps = {
  accessoryItem?: AccessoryItemProps;
  buttons: HeaderButtonProps[];
  deviceWidth?: number;
  headerLinks: HeaderLinksProps[];
  isLoggedIn?: boolean;
  loginUrl: string;
  onLogoutClick?: () => void;
  userButton?: UserButtonProps;
  searchBar?: SearchBarProps;
};

const LargeHeader = forwardRef<HTMLInputElement, FullHeaderProps>(
  (
    {
      accessoryItem,
      headerLinks,
      isLoggedIn,
      buttons,
      loginUrl,
      userButton,
      onLogoutClick,
      searchBar,
    }: FullHeaderProps,
    ref
  ) => (
    <>
      {searchBar && <HeaderSearchBar {...searchBar} ref={ref} />}
      <MenuItems headerLinks={headerLinks} />
      {accessoryItem && <AccessoryItem {...accessoryItem} />}
      {isLoggedIn && userButton && (
        <HeaderAvatar {...userButton} onLogoutClick={onLogoutClick} />
      )}
      {!isLoggedIn && (
        <HeaderButton
          buttonType="link"
          label="Log In"
          target="_blank"
          variation="outline"
          emphasis="secondary"
          href={loginUrl}
        />
      )}
      <HeaderButtons buttons={buttons} />
    </>
  )
);

export type HeaderProps = {
  /**
   * An item that will be collapsed into the sub menu only at a size below 768px.
   */
  accessoryItem?: AccessoryItemProps;
  /**
   * Determines whether the logo will be dynamic and show text depending on the
   * width or only ever show the logo without text
   */
  displayLogoIcon?: boolean;
  /**
   * Links for the header
   */
  headerLinks?: HeaderLinksProps[];
  /**
   * Sets the page destination of the login button inside sub menus
   */
  loginUrl: string;
  /**
   * Displays the header as logged in or logged out
   */
  isLoggedIn?: boolean;
  /**
   * This is a callback function prop that will fire the passed in action when the log out button is clicked.
   */
  onLogoutClick?: () => void;
  /**
   * An array of buttons. They have the same props as the "Button" with the
   * addition of "label" (string) as the text label on the button.
   */
  buttons?: HeaderButtonProps[];
  /**
   * Controls the theme of the header
   */
  theme?: Theme;
  /**
   * Defines the avatar and its menus if the user is logged in
   */
  userButton?: UserButtonProps;
  /**
   * Defines the search bar either visible or not
   */
  searchBar?: SearchBarProps;
} & React.HTMLAttributes<HTMLDivElement>;

export const Header = forwardRef<HTMLInputElement, HeaderProps>(
  (
    {
      displayLogoIcon,
      headerLinks = [],
      isLoggedIn,
      buttons = [],
      accessoryItem,
      userButton,
      loginUrl = "",
      theme = "core",
      onLogoutClick,
      searchBar,
    }: HeaderProps,
    ref
  ) => {
    const deviceWidth = useDeviceWidth();
    const { desktop, tabletLarge } = breakpoints;
    const isCollapsedHeader = deviceWidth < tabletLarge;
    const isFullIconLogo = deviceWidth >= desktop && !displayLogoIcon;
    const mainMenuType = isFullIconLogo ? "full" : "icon";
    const logoWidth = isFullIconLogo
      ? "190px"
      : isCollapsedHeader
      ? "30px"
      : "40px";

    return (
      <header className={styles.header}>
        <a href="/">
          <Logo type={mainMenuType} width={logoWidth} />
        </a>
        <nav className={styles.nav}>
          {isCollapsedHeader ? (
            <CollapsedHeader
              headerLinks={headerLinks}
              isLoggedIn={isLoggedIn}
              deviceWidth={deviceWidth}
              accessoryItem={accessoryItem}
              buttons={buttons}
              userButton={userButton}
              loginUrl={loginUrl}
              onLogoutClick={onLogoutClick}
              theme={theme}
              searchBar={searchBar}
              ref={ref}
            />
          ) : (
            <LargeHeader
              headerLinks={headerLinks}
              isLoggedIn={isLoggedIn}
              buttons={buttons}
              accessoryItem={accessoryItem}
              loginUrl={loginUrl}
              userButton={userButton}
              onLogoutClick={onLogoutClick}
              searchBar={searchBar}
              ref={ref}
            />
          )}
        </nav>
      </header>
    );
  }
);
